import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import axios from 'axios';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthFromVerification } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setErrorMessage('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-email`, {
        token: token
      });
      
      // Set auth state with the returned token and user
      if (response.data.token && response.data.user) {
        setAuthFromVerification(response.data.token, response.data.user);
      }
      
      setStatus('success');
      toast.success('Email verified successfully!');
      
      // Redirect to stored return URL or role-based default
      const returnUrl = localStorage.getItem('returnAfterAuth');
      if (returnUrl) {
        localStorage.removeItem('returnAfterAuth');
      }
      
      // Role-based redirect: regular users go to profile, hosts/admins go to dashboard
      const defaultRedirect = (response.data?.user?.role === 'host' || response.data?.user?.role === 'admin') 
        ? '/dashboard' 
        : '/profile';
      
      setTimeout(() => {
        navigate(returnUrl || defaultRedirect);
      }, 2000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Verification failed. The link may be expired or invalid.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F4EDE4] flex flex-col" data-testid="verify-email-page">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white border-b py-4 px-6">
        <Logo size={34} />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            
            {/* Verifying State */}
            {status === 'verifying' && (
              <>
                <div className="w-16 h-16 bg-[#FDF8F4] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-[#D65A31] animate-spin" />
                </div>
                <h1 className="text-2xl font-semibold mb-2" data-testid="verifying-title">Verifying your email...</h1>
                <p className="text-gray-500">Please wait while we confirm your email address.</p>
              </>
            )}

            {/* Success State */}
            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h1 className="text-2xl font-semibold mb-2 text-green-700" data-testid="success-title">Email Verified!</h1>
                <p className="text-gray-500 mb-6">
                  Your email has been successfully verified. Redirecting you now...
                </p>
                <Link to={localStorage.getItem('returnAfterAuth') || '/profile'}>
                  <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="go-to-dashboard">
                    Continue
                  </Button>
                </Link>
              </>
            )}

            {/* Error State */}
            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl font-semibold mb-2 text-red-700" data-testid="error-title">Verification Failed</h1>
                <p className="text-gray-500 mb-6">{errorMessage}</p>
                
                <div className="space-y-3">
                  <Link to="/register" className="block">
                    <Button variant="outline" className="w-full" data-testid="try-again-btn">
                      <Mail className="w-4 h-4 mr-2" />
                      Request new verification link
                    </Button>
                  </Link>
                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full text-[#D65A31]" data-testid="back-to-login">
                      Back to login
                    </Button>
                  </Link>
                </div>
              </>
            )}

          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyEmailPage;
